import React, { useEffect, useState } from 'react'
import usePMV from '../../hooks/usePMV';
import PreviewComp from './Preview';
import { FormState } from './Aspects';
import { IPmv } from '../../models/Pmv';
import ButtonIcon from '../../components/ButtonComp';
import { faEdit, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import PaginationComp from '../../components/Pagination';
import { Col, Row, Skeleton, Space, TreeProps } from 'antd';
import { IAsset } from '../../models/Assets';
import useConfirm from '../../hooks/useConfirm';
import { sendPostRequestApi } from '../../api/its/pmv';
import { useMutation } from '@apollo/client';
import { INSERT_PMV_STATUS } from 'graphql/mutations/pmv_status';
import { useNotificationContext } from 'services/NotificationContext';

interface IStoreItem {
    pmv: IPmv;
    checkedKeys: TreeProps['checkedKeys'];
    assetsPMV: IAsset[];
    hookPMV: ReturnType<typeof usePMV>;
    setActiveKey: (key: string) => void;
}

interface IStoreComp {
    checkedKeys: TreeProps['checkedKeys'];
    assetsPMV: IAsset[];
    hookPMV: ReturnType<typeof usePMV>;
    setActiveKey: (key: string) => void;
}

const StoreItem: React.FC<IStoreItem> = ({ pmv, checkedKeys, assetsPMV, hookPMV, setActiveKey }: IStoreItem) => {
    const { show } = useNotificationContext();
    const { showPromiseConfirm } = useConfirm();
    const [formState, setFormState] = useState<FormState[]>([{ messages: [], pictogram: "none", textAlign: "center", textColor: "#fff", textSize: 50 }]);
    const [currentAspectIndex, setCurrentAspectIndex] = useState<number>(0);
    const [insertPmvStatus] = useMutation(INSERT_PMV_STATUS);

    useEffect(() => {
        if (pmv) {
            setFormState(pmv.aspects.map((aspect) => {
                const { line1, line2, line3, textAlign, pictogram } = aspect;
                return {
                    messages: [line1, line2, line3],
                    pictogram,
                    textSize: 30,
                    textColor: "#ffffff",
                    textAlign
                }
            }));
        }
    }, [pmv]);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentAspectIndex((prevIndex) => (prevIndex + 1) % formState.length);
        }, 2000); // Cambiar cada 2 segundos
        return () => clearInterval(interval); // Limpiar el intervalo al desmontar el componente
    }, [formState.length]);

    // Enviar la configuración a los paneles seleccionados
    const handleSendMessage = async () => {
        const content = (
            <>
                <p>¿Está seguro que desea actualizar estos paneles?</p>
                <ul>
                    {Array.isArray(checkedKeys) && checkedKeys.map(panel_id => {
                        const panel = assetsPMV.find(asset => asset.id === panel_id);
                        return <li key={panel_id}>{panel?.description}</li>
                    })}
                </ul>
            </>
        );

        showPromiseConfirm({
            title: 'Actualizar panel(es)',
            content,
            okText: 'Actualizar',
            actionCallback: async () => {
                if (Array.isArray(checkedKeys))
                    for (const panel of assetsPMV.filter(asset => checkedKeys.includes(asset.id))) {
                        const configuration = formState.map((aspect) => {
                            const lines = aspect.messages.map((message, i) =>
                                message.trim()
                                    ? `[tr65,${i * 16 + 1},0,0]${message.toLocaleUpperCase().padEnd(18, " ").slice(0, 18)}`
                                    : `[tr65,${i * 16 + 1},0,0]`
                            );
                            // Extraer el número del pictograma si existe
                            const regex = /\/static\/media\/(\d+)\./;
                            const match = aspect.pictogram.match(regex);
                            const pictogramNumber = match ? match[1] : null;

                            const pictogramTag = pictogramNumber ? `[g${pictogramNumber}]` : "";
                            const aspectTagStart = formState.length > 1 ? "[pt10o0]" : "";
                            const aspectTagEnd = formState.length > 1 ? "[np]" : "";

                            return [aspectTagStart, pictogramTag, ...lines, aspectTagEnd].filter(Boolean).join("");
                        });

                        console.log(panel, "Configuración generada:\n\n" + configuration.join(""));
                        const response = await sendPostRequestApi(configuration.join(""), panel.id);
                        if (response.status == 200) {
                            if (response.message.trim() === configuration.join("").trim()) {
                                // Insertar registro en la base de datos
                                const responseDB = await insertPmvStatus({ variables: { message: configuration.join(""), pmv_id: panel.id } });
                                show("success", "Panel '" + panel.description + "' actualizado", "La configuración del panel ha sido actualizada correctamente.");
                            } else {
                                show("warning", "Panel '" + panel.description + "' se ha intentado actualizar", "Es posible que la configuración del panel no haya sido actualizada correctamente.");
                            }
                        } else {
                            show("error", "Error al actualizar el panel", "Es posible que el panel '" + panel.description + "' no haya podido ser actualizado.");
                        }
                    }
            }
        });
    }

    const handleEdit = (pmv: IPmv) => {
        hookPMV.selectItem(pmv);
        setActiveKey("1")
    }

    return (
        <Col
            xs={24}
            style={{
                maxWidth: "35rem",
                minWidth: "30rem"
            }}
        >
            <PreviewComp
                formState={formState}
                currentAspectIndex={currentAspectIndex}
            />
            <div className='d-flex justify-content-end mb-4'>
                <Space>
                    <ButtonIcon icon={faEdit} name="Editar" variant="outline-success" size="sm" onClick={() => handleEdit(pmv)} />
                    <ButtonIcon icon={faPaperPlane} name="Enviar a panel(es)" variant="success" size="sm" onClick={handleSendMessage} />
                </Space>
            </div>
        </Col>
    )
}

const StoredComp: React.FC<IStoreComp> = ({ checkedKeys, assetsPMV, hookPMV, setActiveKey }: IStoreComp) => {
    // Consultar paneles del paquete Guadalajara Colima
    // const hookPMV = usePMV({ package_id: 17 });

    return (
        <div>
            {/* Mostrar vista previa de todos los paneles de base de datos */}
            <PaginationComp
                page={hookPMV.page}
                defaultPageSize={hookPMV.pageSize}
                onChange={hookPMV.changePage}
                totalItems={hookPMV.totalItems}
            />
            <Row
                gutter={[16, 16]}
                justify="center"
                style={{ maxHeight: '80vh', overflowY: 'auto' }}
            >
                {
                    hookPMV.loading && <Skeleton active />
                }
                {
                    hookPMV.data.map((pmv) =>
                        <StoreItem
                            key={pmv.id}
                            pmv={pmv}
                            checkedKeys={checkedKeys}
                            assetsPMV={assetsPMV}
                            hookPMV={hookPMV}
                            setActiveKey={setActiveKey}
                        />
                    )
                }
            </Row>
        </div>
    )
}

export default StoredComp
